import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { Loading } from '../UI';
import { DatePicker } from '../form';
import { Checkbox } from '../form';
import { useUserStrategies } from '../../context/UserStrategies';
import { useStrategiesContext } from './StrategiesContext';
import { UPDATE_STRATEGY } from '../../http/apollo-queries';
import { FooterNavigator } from '../Footer/FooterNavigator';
import InfoTooltip from '../InfoTooltip/Info-tooltip';

export const AdvancedSettings = ({
  strategyId,
  close,
}: {
  strategyId?: string;
  close?(): void;
}) => {
  const { t } = useTranslation();

  const {
    loading: loadingStrategies,
    strategies,
    refetch: refetchStrategies,
  } = useUserStrategies();

  const { strategy: contextStrategy, closeModal } = useStrategiesContext();

  const [updateStrategy, { loading: updateStrategyLoading }] =
    useMutation(UPDATE_STRATEGY);

  const strategy = strategyId
    ? strategies.find(({ id }) => id === strategyId)
    : contextStrategy;

  const loading = updateStrategyLoading || loadingStrategies;

  const formik = useFormik<{
    startDate: string;
    endDate: string;
    sendAlert?: boolean;
  }>({
    initialValues: {
      startDate: strategy?.startDate as string,
      endDate: strategy?.endDate as string,
      sendAlert: strategy?.notifyEnd,
    },
    onSubmit: (values) => {
      const promises: Promise<any>[] = [];

      if (
        values.startDate !== strategy?.startDate ||
        values.endDate !== strategy?.endDate ||
        values.sendAlert !== strategy?.notifyEnd
      ) {
        promises.push(
          updateStrategy({
            variables: {
              input: {
                id: strategy?.id,
                name: strategy?.name,
                currency: strategy?.currency,
                color: strategy?.color,
                startDate: values.startDate,
                endDate: values.endDate,
                notifyEnd: values.sendAlert,
              },
            },
          })
        );
      }

      Promise.all(promises).then(() => {
        refetchStrategies();
        close ? close() : closeModal();
      });
    },
  });

  return !strategy ? (
    <Loading />
  ) : (
    <div className="flex flex-col h-full w-full justify-between overflow-y-auto pb-4">
      <div className={'px-20'}>
        <h1 className="text-center font-semibold text-xl my-6">
          {t('modals.advancedSettings.title')}
        </h1>

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            <div>
              <WithTooltip content={''} className={'mb-6'}>
                <p className="font-medium">
                  {t('modals.advancedSettings.strategyTimeframe')}
                </p>
              </WithTooltip>
              <p className="text-sm ">
                {t('modals.advancedSettings.strategyTimeframe')}
              </p>
              <DatePicker
                isRangePicker
                size="medium"
                startDate={moment(formik.values.startDate).toDate()}
                endDate={moment(formik.values.endDate).toDate()}
                id={'strategyTimeframe'}
                onChange={(range) => {
                  if (Array.isArray(range)) {
                    const startDate = moment(range[0]).format('YYYY-MM-DD');
                    const endDate = moment(range[1]).format('YYYY-MM-DD');
                    formik.setFieldValue('startDate', startDate);
                    formik.setFieldValue('endDate', endDate);
                  }
                }}
              />

              <Checkbox
                shape="square"
                className="my-6"
                isCheck={formik.values.sendAlert}
                onClick={() =>
                  formik.setFieldValue('sendAlert', !formik.values.sendAlert)
                }
              >
                {t('modals.advancedSettings.sendAlert')}
              </Checkbox>
            </div>
          </form>
        </FormikProvider>
      </div>
      {/* footer */}
      <FooterNavigator
        loading={loading}
        className={`
          border-t
          left-0
          right-0
          bottom-0
          rounded-b-xl
          border-grey-10
          bg-white
          flex-grow-0
          flex-shrink-0
        `}
        onCancel={close ?? closeModal}
        onSave={() => formik.submitForm()}
      />
    </div>
  );
};

function WithTooltip({ content, children, className = '' }) {
  return (
    <div className={'flex items-center gap-3 ' + className}>
      {children}
      <InfoTooltip content={content} isTooltipIconVisible />
    </div>
  );
}
